import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Eye, EyeOff, X } from 'lucide-react';
import NexovernLogo from '@/assets/nexovern-full.png';
import { apiClient } from '@/lib/axios';
import { LOGIN_PASSWORD_MAX_LENGTH, LOGIN_PASSWORD_MIN_LENGTH } from '@/lib/password-policy';
import { Button } from '@/components/ui';

// Same policy as the rest of the app (see password-policy.ts); the backend's
// ResetPasswordDto remains the source of truth.
const PASSWORD_RULES = [
  {
    label: `${LOGIN_PASSWORD_MIN_LENGTH}–${LOGIN_PASSWORD_MAX_LENGTH} characters`,
    test: (p: string) =>
      p.length >= LOGIN_PASSWORD_MIN_LENGTH && p.length <= LOGIN_PASSWORD_MAX_LENGTH,
  },
  { label: 'An uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
  { label: 'A number', test: (p: string) => /\d/.test(p) },
  { label: 'A special character', test: (p: string) => /[^A-Za-z0-9]/.test(p) },
];

const TOKEN_PATTERN = /^[0-9a-f]{64}$/i;
const REDIRECT_DELAY_MS = 3000;

type LinkState = 'checking' | 'valid' | 'invalid';

function apiErrorMessage(err: unknown, fallback: string): string {
  const message = (err as { response?: { data?: { message?: string | string[] } } })?.response?.data
    ?.message;
  if (Array.isArray(message)) return message[0] ?? fallback;
  return message ?? fallback;
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [linkState, setLinkState] = useState<LinkState>(
    TOKEN_PATTERN.test(token) ? 'checking' : 'invalid',
  );
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Check the link up front so an expired one says so immediately, instead of
  // only after the user has typed a new password twice.
  useEffect(() => {
    if (!TOKEN_PATTERN.test(token)) return;
    let cancelled = false;
    apiClient
      .post<{ valid: boolean }>('/auth/reset-password/validate', { token })
      .then(({ data }) => {
        if (!cancelled) setLinkState(data.valid ? 'valid' : 'invalid');
      })
      .catch(() => {
        // Don't strand the user on a spinner — let them try; submit re-checks.
        if (!cancelled) setLinkState('valid');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => navigate('/login', { replace: true }), REDIRECT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [success, navigate]);

  const rulesMet = PASSWORD_RULES.every((rule) => rule.test(newPassword));
  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!rulesMet) {
      setError('Your new password does not meet all the requirements below.');
      return;
    }
    if (!passwordsMatch) {
      setError('New password and confirm password do not match.');
      return;
    }

    setIsSubmitting(true);
    try {
      const { data } = await apiClient.post<{ message: string }>('/auth/reset-password', {
        token,
        newPassword,
        confirmPassword,
      });
      setSuccess(data.message);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const message = apiErrorMessage(err, 'Failed to update password. Please try again.');
      // A dead link can't be fixed by retyping — switch to the expired view.
      if (status === 400 && /invalid or has expired/i.test(message)) {
        setLinkState('invalid');
      } else if (status === 429) {
        setError('Too many attempts. Please wait a minute and try again.');
      } else {
        setError(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center justify-center">
          <img src={NexovernLogo} alt="Nexovern" className="h-12 w-auto" />
        </div>

        <div className="rounded-xl border border-border-subtle bg-surface-0 p-6 shadow-[var(--shadow-floating)]">
          {linkState === 'checking' ? (
            <div className="flex items-center justify-center py-8" role="status" aria-label="Checking link">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-accent border-t-transparent" />
            </div>
          ) : linkState === 'invalid' ? (
            <>
              <h2 className="mb-3 text-sm font-semibold text-text-primary">Link expired or invalid</h2>
              <p className="mb-5 text-xs text-text-secondary">
                This password reset link is no longer valid. Links expire after 15 minutes and can only
                be used once. Request a new one from the sign-in page.
              </p>
              <Link
                to="/login"
                className="block w-full rounded-md bg-accent px-3 py-2 text-center text-sm font-medium text-white hover:opacity-90"
              >
                Request a new link
              </Link>
            </>
          ) : success ? (
            <>
              <h2 className="mb-3 text-sm font-semibold text-text-primary">Password updated</h2>
              <div
                role="status"
                className="mb-5 rounded-md border border-state-success/40 bg-state-success/10 px-3 py-2 text-xs text-state-success"
              >
                {success} Redirecting you to sign in…
              </div>
              <Link
                to="/login"
                replace
                className="block w-full text-center text-xs font-medium text-text-secondary hover:text-text-primary hover:underline"
              >
                Go to sign in now
              </Link>
            </>
          ) : (
            <>
              <h2 className="mb-1 text-sm font-semibold text-text-primary">Choose a new password</h2>
              <p className="mb-5 text-xs text-text-tertiary">
                You'll be signed out of all other sessions once it's changed.
              </p>

              {error && (
                <div
                  role="alert"
                  className="mb-4 rounded-md border border-state-danger/40 bg-state-danger/10 px-3 py-2 text-xs text-state-danger"
                >
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="new-password" className="mb-1.5 block text-xs font-medium text-text-secondary">
                    New password
                  </label>
                  <div className="relative">
                    <input
                      id="new-password"
                      type={showNewPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      required
                      maxLength={LOGIN_PASSWORD_MAX_LENGTH}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      aria-describedby="password-rules"
                      className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 pr-10 text-sm text-text-primary placeholder:text-text-tertiary focus:border-border-focus focus:outline-none focus:ring-2 focus:ring-border-focus/20"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword((v) => !v)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-secondary"
                      aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                    >
                      {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <ul id="password-rules" className="mt-2 space-y-1">
                    {PASSWORD_RULES.map((rule) => {
                      const met = rule.test(newPassword);
                      return (
                        <li
                          key={rule.label}
                          className={`flex items-center gap-1.5 text-[11px] ${met ? 'text-state-success' : 'text-text-tertiary'}`}
                        >
                          {met ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                          {rule.label}
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div>
                  <label htmlFor="confirm-password" className="mb-1.5 block text-xs font-medium text-text-secondary">
                    Confirm new password
                  </label>
                  <div className="relative">
                    <input
                      id="confirm-password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      required
                      maxLength={LOGIN_PASSWORD_MAX_LENGTH}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 pr-10 text-sm text-text-primary placeholder:text-text-tertiary focus:border-border-focus focus:outline-none focus:ring-2 focus:ring-border-focus/20"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-secondary"
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {confirmPassword.length > 0 && !passwordsMatch && (
                    <p className="mt-1.5 text-xs text-state-danger">Passwords do not match.</p>
                  )}
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  fullWidth
                  loading={isSubmitting}
                  disabled={!rulesMet || !passwordsMatch}
                >
                  Update password
                </Button>

                <Link
                  to="/login"
                  className="block w-full text-center text-xs font-medium text-text-secondary hover:text-text-primary hover:underline"
                >
                  Back to Sign in
                </Link>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
